'use strict';

const libWorkspace = require('pict-section-workspace');
const libLocationTree = require('headlight-sample-scanning').LocationTree;
const libSampleLocationCartSession = require('../samples/SampleLocationCart-Session.js');

// The Location page's own cart for adding samples here: separate from the Sample page's and the panels' carts.
const SAMPLE_CART_NAME = 'LocationSampleCart';
const SAMPLE_CART_HOST_ID = 'LocationWorkspace-SampleCart';

/**
 * @typedef {Object} LocationRecord
 * @property {number} IDLocation
 * @property {string} [GUIDLocation]
 * @property {number} [ParentIDLocation]
 * @property {string} [Name]
 * @property {string} [Type]
 * @property {string} [QRCode]
 * @property {number|string} [Latitude]
 * @property {number|string} [Longitude]
 * @property {number} [IDObservation] - the location's photo (an image observation), 0 when it has none
 */

/**
 * @typedef {Object} LocationSamplesState
 * @property {boolean} Loaded
 * @property {boolean} Loading
 * @property {string} Error
 * @property {Array<Record<string, any>>} Samples
 * @property {Array<number>} LocationIDs
 * @property {boolean} Truncated
 */

/**
 * The bespoke methods behind workspace-configs/Location.config.js: where the location sits in the location tree,
 * its photo and where it is on a map, the samples kept here or in any location inside it, printing its label and
 * deleting it.
 */
class LocationWorkspaceProvider extends libWorkspace.HeadlightWorkspaceBaseProvider
{
	/**
	 * @param {any} pFable
	 * @param {any} pOptions
	 * @param {string} pServiceHash
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		/** @type {Array<LocationRecord>} */
		this._locations = [];
		/** @type {LocationSamplesState} */
		this._samples = this._emptySamplesState();
	}

	/**
	 * @return {LocationSamplesState}
	 */
	_emptySamplesState()
	{
		return { Loaded: false, Loading: false, Error: '', Samples: [], LocationIDs: [], Truncated: false };
	}

	/**
	 * @return {any} the confirmation modal, when the app has one
	 */
	get modal()
	{
		return this.pict.views['Pict-Section-Modal'];
	}

	/**
	 * @return {boolean}
	 */
	canUpdate()
	{
		return this.can('Location', 'Update');
	}

	/**
	 * @return {boolean}
	 */
	canDelete()
	{
		return this.can('Location', 'Delete');
	}

	/**
	 * Called by the base after the location record loads: reset the samples tab and read the customer's locations
	 * for the breadcrumb and the locations inside this one.
	 *
	 * @return {Promise<void>}
	 */
	async hydrateAsync()
	{
		this.sampleCart.close();
		this._addingSamples = false;
		this._samples = this._emptySamplesState();
		const tmpLocationProvider = this.pict.providers.LocationProvider;
		if (!tmpLocationProvider || (typeof tmpLocationProvider.getAllLocations !== 'function'))
		{
			this._locations = [];
			return;
		}
		try
		{
			this._locations = await tmpLocationProvider.getAllLocations();
		}
		catch (pError)
		{
			this.pict.log.warn(`LocationWorkspace: could not read the location list: ${(pError && pError.message) || pError}`);
			this._locations = [];
		}
	}

	/**
	 * @param {string} pSize - Thumbnail, Standard, Preview or Enhanced
	 * @return {string} the URL of the location's photo at that size, through GET Location/:IDLocation/Photo/:Size
	 */
	photoURL(pSize)
	{
		const tmpPath = `Location/${Number(this.record.IDLocation)}/Photo/${pSize}`;
		const tmpRestClient = this.restClient;
		if (tmpRestClient && (typeof tmpRestClient.formatUrl === 'function'))
		{
			try
			{
				return tmpRestClient.formatUrl(tmpPath);
			}
			catch (pError)
			{
				// fall through to the relative URL
			}
		}
		return `/1.0/${tmpPath}`;
	}

	/**
	 * The Details tab's Photo field: the location's photo (opening full size in a new tab), or "No photo."
	 *
	 * @return {string}
	 */
	photoHTML()
	{
		if (!(Number(this.record.IDObservation) > 0))
		{
			return 'No photo.';
		}
		const tmpName = this.record.Name || `Location ${this.record.IDLocation}`;
		// A just-uploaded photo can take a moment to process; say so instead of showing a broken image.
		return `<a class="locw-photo-link" href="${this._esc(this.photoURL('Standard'))}" target="_blank" rel="noopener">` +
			`<img class="locw-photo" src="${this._esc(this.photoURL('Preview'))}" alt="Photo of ${this._esc(tmpName)}" ` +
			`onerror="this.parentNode.classList.add('is-hidden'); this.parentNode.nextElementSibling.classList.remove('is-hidden');" /></a>` +
			`<span class="pw-muted is-hidden">The photo is still being processed. It will show here shortly.</span>`;
	}

	/**
	 * @return {{ lat: number, lng: number }|null} the location's coordinates, when it has real ones (not blank, not
	 *     0,0, within range)
	 */
	coordinates()
	{
		const tmpRecord = this.record;
		if ((tmpRecord.Latitude === null) || (tmpRecord.Latitude === undefined) || (tmpRecord.Latitude === '') ||
			(tmpRecord.Longitude === null) || (tmpRecord.Longitude === undefined) || (tmpRecord.Longitude === ''))
		{
			return null;
		}
		const tmpLat = Number(tmpRecord.Latitude);
		const tmpLng = Number(tmpRecord.Longitude);
		if (!Number.isFinite(tmpLat) || !Number.isFinite(tmpLng) || (Math.abs(tmpLat) > 90) || (Math.abs(tmpLng) > 180) || ((tmpLat === 0) && (tmpLng === 0)))
		{
			return null;
		}
		return { lat: tmpLat, lng: tmpLng };
	}

	/**
	 * What the LocationViewMap plots: one read-only marker at the location.
	 * @return {{ capture?: { lat: number, lng: number, label: string } }}
	 */
	mapPoints()
	{
		const tmpPoint = this.coordinates();
		return tmpPoint ? { capture: Object.assign({ label: this.record.Name || 'Location' }, tmpPoint) } : {};
	}

	/**
	 * The Details tab's Map field: the coordinates over a read-only map, or that none are set.
	 *
	 * @return {string}
	 */
	mapHTML()
	{
		const tmpPoint = this.coordinates();
		if (!tmpPoint)
		{
			return 'No location set.';
		}
		return `<div class="locw-coords"><i class="fas fa-location-dot"></i> ${tmpPoint.lat.toFixed(6)}, ${tmpPoint.lng.toFixed(6)}</div>` +
			'<div id="LW-MapHost" class="locw-map"></div>';
	}

	/**
	 * Draw the Details tab, then mount the map into it. The overview is drawn again when its Inside card resolves,
	 * which replaces the map's host, so the map is mounted after every draw.
	 *
	 * @param {string} pBodySel
	 */
	_renderOverview(pBodySel)
	{
		super._renderOverview(pBodySel);
		this.mountMap();
	}

	/** Mount the read-only map into the Details tab's map host, when it is on the page. */
	mountMap()
	{
		if ((typeof document === 'undefined') || !document.getElementById('LW-MapHost'))
		{
			return;
		}
		const tmpMap = this.pict.views.LocationViewMap;
		if (tmpMap && (typeof tmpMap.showForProject === 'function'))
		{
			tmpMap.showForProject();
		}
	}

	// --- header QR thumbnail ---------------------------------------------------------------------------------------

	/** @return {string} the header corner's QR thumbnail host, when viewing a location that has a QR code */
	headerExtra()
	{
		return ((this.mode === 'view') && this.record.QRCode) ? '<div class="locw-qr-thumb" id="LocationWorkspace-QR"></div>' : '';
	}

	/** Draw the location's QR code into the header thumbnail, the way the Sample page draws a sample's. */
	headerAfterRender()
	{
		const tmpQR = this.pict.providers.HeadlightQR;
		if ((this.mode !== 'view') || !this.record.QRCode || !tmpQR || (typeof document === 'undefined'))
		{
			return;
		}
		try
		{
			const tmpContainer = document.querySelector('#LocationWorkspace-QR');
			if (tmpContainer)
			{
				tmpContainer.innerHTML = tmpQR.renderSVG(tmpQR.encode('Location', String(this.record.QRCode)), { SizePX: 128, Title: `QR ${this.record.QRCode}` });
			}
		}
		catch (pError)
		{
			// The thumbnail is decorative.
		}
	}

	// --- Samples tab: adding samples with this page's cart ----------------------------------------------------------

	/** @return {any} the Samples tab's cart session (this page's own cart, fixed to this location) */
	get sampleCart()
	{
		if (!this._sampleCart)
		{
			this._sampleCart = new libSampleLocationCartSession(
				{
					Pict: () => this.pict,
					CartName: SAMPLE_CART_NAME,
					HostID: SAMPLE_CART_HOST_ID,
					OwnHash: () => `#/Location/Workspace/${Number(this.record.IDLocation)}/samples`,
					Toast: (pMessage, pType) => this._toast(pMessage, pType),
					OnMoved: (pMove) => this._onSamplesMoved(pMove),
					OnLeave: () =>
					{
						this._addingSamples = false;
						this.refreshSamples();
					},
				});
		}
		return this._sampleCart;
	}

	/** @return {boolean} whether the user may move samples into this location */
	canAddSamples()
	{
		return (this.mode === 'view') && this.can('Sample', 'Update');
	}

	/** Add samples: swap the samples list for the cart, fixed to this location and empty. */
	addSamples()
	{
		if (!this.canAddSamples())
		{
			return;
		}
		this._addingSamples = true;
		this.repaintBody();
	}

	/** Close the cart and show the samples list again, read afresh. */
	closeSampleCart()
	{
		this.sampleCart.close();
		this._addingSamples = false;
		this.refreshSamples();
	}

	/**
	 * The cart moved samples here. Move all finishes adding: close the cart and show the list again. A scan saved
	 * right away keeps the cart open; the list is read again when it closes.
	 * @param {{ Moved: Array<Record<string, any>>, Via: string }} pMove
	 */
	_onSamplesMoved(pMove)
	{
		if (this._addingSamples && pMove && (pMove.Via === 'move-all') && Array.isArray(pMove.Moved) && pMove.Moved.length)
		{
			this.closeSampleCart();
		}
	}

	/** Forget the samples list and read it again, redrawing the tab. */
	refreshSamples()
	{
		this._samples = this._emptySamplesState();
		if (this._activeTab === 'samples')
		{
			this.repaintBody();
		}
		return this.ensureSamplesAsync();
	}

	/** @return {string} the Samples tab's toolbar: Add samples, when the user may */
	_samplesToolbarHTML()
	{
		if (!this.canAddSamples())
		{
			return '';
		}
		return '<div class="locw-toolbar"><button class="button is-small is-link" onclick="_Pict.providers.LocationWorkspace.addSamples()">' +
			'<span class="icon"><i class="fas fa-plus"></i></span><span>Add samples</span></button></div>';
	}

	/**
	 * @param {LocationRecord} pLocation
	 * @return {string} a link to that location's workspace
	 */
	_locationLinkHTML(pLocation)
	{
		const tmpName = pLocation.Name || `Location ${pLocation.IDLocation}`;
		return `<a href="#/Location/Workspace/${Number(pLocation.IDLocation)}">${this._esc(tmpName)}</a>`;
	}

	/**
	 * The header line under the name: the location's type and the locations it sits inside, outermost first.
	 *
	 * @return {string}
	 */
	headerSubtitle()
	{
		const tmpRecord = this.record;
		const tmpParts = [];
		if (tmpRecord.Type)
		{
			tmpParts.push(`<span class="locw-type">${this._esc(tmpRecord.Type)}</span>`);
		}
		const tmpAncestors = libLocationTree.getAncestors(tmpRecord.IDLocation, this._locations);
		if (tmpAncestors.length > 0)
		{
			tmpParts.push(`<span class="locw-crumbs">Inside ${tmpAncestors.map((pLocation) => this._locationLinkHTML(pLocation)).join(' <span class="locw-sep">›</span> ')}</span>`);
		}
		if (tmpRecord.QRCode)
		{
			tmpParts.push(`<span class="locw-qr"><i class="fas fa-qrcode"></i> ${this._esc(tmpRecord.QRCode)}</span>`);
		}
		return tmpParts.length ? `<div class="locw-subline">${tmpParts.join('')}</div>` : '';
	}

	/**
	 * Samples tab body.
	 *
	 * @return {string}
	 */
	renderSamples(pProvider, pBodySelector)
	{
		if (this._addingSamples)
		{
			// The cart draws into the tab itself, so the body is assigned here rather than returned.
			const tmpName = this.record.Name || `Location ${this.record.IDLocation}`;
			this.pict.ContentAssignment.assignContent(pBodySelector || this.bodySelector,
				`<div class="locw-toolbar locw-cart-bar"><span class="pw-muted">Scan or collect samples to move into ${this._esc(tmpName)}, then Move all.</span>` +
				'<button class="button is-small is-light" onclick="_Pict.providers.LocationWorkspace.closeSampleCart()"><span class="icon"><i class="fas fa-xmark"></i></span><span>Close</span></button></div>' +
				`<div id="${SAMPLE_CART_HOST_ID}" class="locw-cart"></div>`);
			this.sampleCart.mount({ FixedLocation: this.record });
			return undefined;
		}
		const tmpChildren = libLocationTree.getChildren(this.record.IDLocation, this._locations);
		const tmpChildHTML = this._samplesToolbarHTML() + (tmpChildren.length ?
			`<p class="locw-inside"><strong>Inside this location:</strong> ${tmpChildren.map((pChild) => this._locationLinkHTML(pChild)).join(', ')}</p>` :
			'');
		const tmpSamples = this._samples;
		if (tmpSamples.Error)
		{
			return `${tmpChildHTML}<div class="locw-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(tmpSamples.Error)}</div>`;
		}
		if (!tmpSamples.Loaded)
		{
			return `${tmpChildHTML}<div class="pw-state"><span class="pw-spinner pw-spinner-inline"></span> Looking up samples…</div>`;
		}
		const tmpCount = tmpSamples.Samples.length;
		const tmpWhere = (tmpSamples.LocationIDs.length > 1) ? ' here or inside' : ' here';
		const tmpSummary = `${tmpCount}${tmpSamples.Truncated ? '+' : ''} sample${(tmpCount === 1) ? '' : 's'}${tmpWhere}.${tmpSamples.Truncated ? ' Showing the first 500.' : ''}`;
		if (tmpCount === 0)
		{
			return `${tmpChildHTML}<div class="pw-empty"><i class="fas fa-inbox"></i><p>No samples are kept${tmpWhere}.</p></div>`;
		}
		const tmpNames = new Map(this._locations.map((pLocation) => [ Number(pLocation.IDLocation), pLocation.Name ]));
		const tmpHere = Number(this.record.IDLocation);
		const tmpRows = tmpSamples.Samples.map((pSample) =>
		{
			const tmpCurrent = Number(pSample.CurrentIDLocation);
			const tmpWhereCell = (tmpCurrent === tmpHere) ?
				'Here' :
				`<a href="#/Location/Workspace/${tmpCurrent}">${this._esc(tmpNames.get(tmpCurrent) || `Location ${tmpCurrent}`)}</a>`;
			return `<tr>` +
				`<td class="pw-cell-strong"><a href="#/Sample/Workspace/${Number(pSample.IDSample)}">${this._esc(pSample.Name || `Sample ${pSample.IDSample}`)}</a></td>` +
				`<td class="pw-muted">${this._esc(pSample.QRCode || '')}</td>` +
				`<td>${this._esc(this._sampleStatusName(pSample.Status))}</td>` +
				`<td>${tmpWhereCell}</td>` +
				`</tr>`;
		}).join('');
		return `${tmpChildHTML}<p class="locw-count">${this._esc(tmpSummary)}</p>` +
			`<div class="pw-tablewrap"><table class="pw-table"><thead><tr><th>Sample</th><th>QR Code</th><th>Status</th><th>Where</th></tr></thead><tbody>${tmpRows}</tbody></table></div>`;
	}

	/**
	 * @param {string} pCode - a stored sample status code
	 * @return {string} its label when the Sample workspace has read the customer's statuses, else the code
	 */
	_sampleStatusName(pCode)
	{
		const tmpSampleWorkspace = this.pict.providers.SampleWorkspace;
		if (pCode && tmpSampleWorkspace && (typeof tmpSampleWorkspace.resolveSampleStatusName === 'function'))
		{
			return tmpSampleWorkspace.resolveSampleStatusName(pCode);
		}
		return pCode || '';
	}

	/**
	 * @return {Promise<void>} resolves once the Sample workspace has read the customer's status names
	 */
	_loadSampleStatusNamesAsync()
	{
		const tmpSampleWorkspace = this.pict.providers.SampleWorkspace;
		if (tmpSampleWorkspace && (typeof tmpSampleWorkspace.ensureStatusVocabularyAsync === 'function'))
		{
			return tmpSampleWorkspace.ensureStatusVocabularyAsync();
		}
		return Promise.resolve();
	}

	/**
	 * Samples tab OnEnter: look the samples up once per opened location, then repaint the tab.
	 *
	 * @return {Promise<void>}
	 */
	async ensureSamplesAsync()
	{
		const tmpSamples = this._samples;
		if (tmpSamples.Loaded || tmpSamples.Loading)
		{
			return;
		}
		const tmpLocationProvider = this.pict.providers.LocationProvider;
		if (!tmpLocationProvider || (typeof tmpLocationProvider.getSamplesAtLocation !== 'function'))
		{
			tmpSamples.Error = 'Samples by location are not available here.';
			this.repaintBody();
			return;
		}
		const tmpIDLocation = Number(this.record.IDLocation);
		tmpSamples.Loading = true;
		try
		{
			const [ tmpResult ] = await Promise.all([ tmpLocationProvider.getSamplesAtLocation(tmpIDLocation, this._locations), this._loadSampleStatusNamesAsync() ]);
			if ((this._samples !== tmpSamples) || (Number(this.record.IDLocation) !== tmpIDLocation))
			{
				return;
			}
			tmpSamples.Samples = tmpResult.Samples;
			tmpSamples.LocationIDs = tmpResult.LocationIDs;
			tmpSamples.Truncated = tmpResult.Truncated;
			tmpSamples.Loaded = true;
		}
		catch (pError)
		{
			this.pict.log.error(`LocationWorkspace: could not read the samples at location ${tmpIDLocation}: ${(pError && pError.message) || pError}`);
			tmpSamples.Error = 'Could not look up the samples at this location. Try again in a moment.';
		}
		finally
		{
			tmpSamples.Loading = false;
		}
		if (this._activeTab === 'samples')
		{
			this.repaintBody();
		}
	}

	/**
	 * @param {string} pMessage
	 * @param {string} pType - 'success' | 'error' | 'info'
	 */
	_toast(pMessage, pType)
	{
		const tmpPanels = this.pict.views.HeadlightPanels;
		if (tmpPanels && (typeof tmpPanels.toast === 'function'))
		{
			tmpPanels.toast({ type: (pType === 'error') ? 'is-danger' : ((pType === 'success') ? 'is-success' : 'is-info'), message: pMessage });
			return;
		}
		this.pict.log.info(`LocationWorkspace: ${pMessage}`);
	}

	/**
	 * Print the location's QR label, the same label legacy LIMS prints.
	 *
	 * @param {boolean} [pShowOptions] - open Print options (label stock and preview) instead of printing right away
	 * @return {Promise<void>}
	 */
	async printLabel(pShowOptions)
	{
		const tmpRecord = this.record;
		const tmpQR = this.pict.providers.QRCodeProvider;
		if (!tmpQR || !tmpRecord.QRCode)
		{
			this._toast('This location has no QR code to print.', 'error');
			return;
		}
		await tmpQR.ensurePrintableManifests();
		if (await tmpQR.printDecoratedEntity('Location', tmpRecord.QRCode, undefined, undefined, tmpRecord, { ShowOptions: !!pShowOptions }) === null)
		{
			this._toast('The label could not be printed. Check your connection and try again.', 'error');
		}
	}

	/**
	 * Ask, then delete this location and go back to the list.
	 *
	 * @return {Promise<void>}
	 */
	async deleteLocation()
	{
		const tmpRecord = this.record;
		if (!tmpRecord.IDLocation)
		{
			return;
		}
		if (!this.canDelete())
		{
			this._toast('You do not have permission to delete locations.', 'error');
			return;
		}
		const tmpName = tmpRecord.Name || `Location ${tmpRecord.IDLocation}`;
		const tmpInside = libLocationTree.getChildren(tmpRecord.IDLocation, this._locations).length;
		const tmpMessage = `Delete “${tmpName}”?${(tmpInside > 0) ? ` ${tmpInside} location${(tmpInside === 1) ? ' is' : 's are'} inside it and will no longer be inside anything.` : ''} This can't be undone.`;
		let tmpConfirmed;
		if (this.modal && (typeof this.modal.confirm === 'function'))
		{
			tmpConfirmed = await this.modal.confirm(tmpMessage, { title: 'Delete this location?', confirmLabel: 'Delete', cancelLabel: 'Cancel' });
		}
		else
		{
			tmpConfirmed = (typeof window !== 'undefined') && window.confirm(tmpMessage);
		}
		if (!tmpConfirmed)
		{
			return;
		}
		const tmpError = await new Promise((fResolve) =>
		{
			this.restClient.deleteEntity('Location', tmpRecord.IDLocation, (pError) => fResolve(pError || null));
		});
		if (tmpError)
		{
			this._toast(`The location could not be deleted: ${(tmpError && tmpError.message) || tmpError}`, 'error');
			return;
		}
		this._toast('Location deleted.', 'success');
		this.backToList();
	}
}

module.exports = LocationWorkspaceProvider;
